import { Filters, SearchMetadata } from "@nerdjs/nerd-core";
import {
  DeleteAction,
  GetAction,
  PostAction,
  PutAction,
} from "@nerdjs/nerd-network";
import { AnyAction, createAction, Dispatch, nanoid } from "@reduxjs/toolkit";
import { Task, Task_Entity } from "../../entities/task";

export const setStagedTask = createAction(
  "setStagedTask",
  function prepare(stagedTask?: Task_Entity) {
    return {
      payload: {
        stagedTask,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const setTaskFormOpen = createAction(
  "setTaskFormOpen",
  function prepare(open: boolean) {
    return {
      payload: {
        open,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const setTaskLoading = createAction(
  "setTaskLoading",
  function prepare(loading: boolean) {
    return {
      payload: {
        loading,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const setTaskSearchOpen = createAction(
  "setTaskSearchOpen",
  function prepare(searchOpen: boolean) {
    return {
      payload: {
        searchOpen,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const setTaskFilters = createAction(
  "setTaskFilters",
  function prepare(filters?: Filters) {
    return {
      payload: {
        filters,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const updateStagedTask = createAction(
  "updateStagedTask",
  function prepare(stagedTask?: Partial<Task_Entity>) {
    return {
      payload: {
        stagedTask,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const setSelectedTaskID = createAction(
  "setSelectedTaskID",
  function prepare(taskID?: number) {
    return {
      payload: {
        taskID,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const getTasks = createAction(
  "kronos/task/getTasks",
  function prepare() {
    const request = new GetAction(`/kronos/tasks`);

    request.decodeResponse = (dispatch: Dispatch, json: unknown) => {
      const tasks = Task.fromList(json);
      dispatch(getTasksSuccess(tasks));
    };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const getTasksSuccess = createAction(
  "kronos/task/getTaskSuccess",
  function prepare(tasks: Task[]) {
    return {
      payload: {
        tasks,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const getTasksByFilter = createAction(
  "kronos/task/getTasksByFilter",
  function prepare(query: { [key: string]: unknown }) {
    const request = new GetAction(`/kronos/tasks`, query);

    request.decodeResponse = (dispatch: Dispatch, json: unknown) => {
      const tasks = Task.fromList(json);
      dispatch(getTasksByFilterSuccess(tasks));
    };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const getTasksByFilterSuccess = createAction(
  "kronos/task/getTasksByFilterSuccess",
  function prepare(tasks: Task[]) {
    return {
      payload: {
        tasks,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const getTaskById = createAction(
  "kronos/task/getById",
  function prepare(id: number) {
    const request = new GetAction(`/kronos/tasks/${id}`);

    request.decodeResponse = (dispatch: Dispatch, json: unknown) => {
      const task = new Task(json);
      dispatch(getTaskByIdSuccess(task));
    };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const getTaskByIdSuccess = createAction(
  "kronos/task/getTaskByIdSuccess",
  function prepare(task?: Task) {
    return {
      payload: {
        task,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const deleteTask = createAction(
  "kronos/removeTaskById",
  function prepare(id: number, successAction?: AnyAction) {
    const request = new DeleteAction(`/kronos/tasks/${id}`);

    if (successAction)
      request.decodeResponse = (dispatch: Dispatch) => {
        dispatch(deleteTaskSuccess(id));
      };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const deleteTaskSuccess = createAction(
  "kronos/tasks/deleteTaskSuccess",
  function prepare(taskID: number) {
    return {
      payload: {
        taskID,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const updateTask = createAction(
  "kronos/tasks/updateTask",
  function prepare(id: number, body: Partial<Task>) {
    const request = new PutAction(`/kronos/tasks/${id}`, body);

    request.decodeResponse = (dispatch: Dispatch) => {
      dispatch(updateTaskSuccess(true));
    };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const updateTaskSuccess = createAction(
  "kronos/tasks/updateTaskSuccess",
  function prepare(success: boolean) {
    return {
      payload: {
        success,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const createTask = createAction(
  "kronos/tasks/createTask",
  function prepare(body: Partial<Task>) {
    const request = new PostAction(`/kronos/tasks`, body);

    request.decodeResponse = (dispatch: Dispatch, json: unknown) => {
      const task = new Task(json);
      dispatch(createTaskSuccess(task));
    };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const createTaskSuccess = createAction(
  "kronos/tasks/createTaskSuccess",
  function prepare(task: Task) {
    return {
      payload: {
        task,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const getTasksSearchMetadata = createAction(
  "kronos/getTasksSearchMetadata",
  function prepare() {
    const request = new GetAction(`/kronos/tasks/searchMetadata`);

    request.decodeResponse = (dispatch: Dispatch, json: SearchMetadata) => {
      dispatch(getTasksSearchMetadataSuccess(json));
    };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const getTasksSearchMetadataSuccess = createAction(
  "kronos/getTasksSearchMetadataSuccess",
  function prepare(tasksSearchMetadata: SearchMetadata) {
    return {
      payload: {
        tasksSearchMetadata,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);
